import { Injectable, inject, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, catchError, map, of, tap } from "rxjs";
import { environment } from "../../../environments/environment";

const STORAGE_KEY = "staffPermissions";

const MENU_MODULES: Record<string, string[]> = {
  reservations: ["reservaciones"],
  wallets: ["puntaje", "puntaje-detalle"],
  branches: ["sucursales"],
  currencies: ["divisas"],
  users: ["clientes"],
  promotions: ["promociones"],
  puntos: ["config-puntos"],
  "referrals-config": ["config-referidos"],
  "referrals-tracking": ["referentes"],
  "point-rules": ["matriz-puntos"],
  marketplace: ["recompensas"],
  "document-requirements": ["documentacion"],
  "home-banners": ["banners"],
  "hero-sections": ["hero"],
  faq: ["faq"],
  roles: ["roles"],
  team: ["equipo"],
  "operating-hours": [
    "horario-base",
    "excepciones",
    "festivos",
    "cierres-temp",
    "cierres-emergencia",
  ],
  "metric-exclusions": ["exclusion-metricas"],
  "app-tokens": ["tokens"],
  "core-environments": ["entornos"],
  "webhook-subscriptions": ["webhooks"],
  settings: ["ajustes"],
  legal: ["terminos"],
};

const ROUTE_MODULES: Array<{ prefix: string; modules: string[] }> = [
  { prefix: "/reservations", modules: ["reservaciones"] },
  { prefix: "/wallets", modules: ["puntaje", "puntaje-detalle"] },
  { prefix: "/branches", modules: ["sucursales"] },
  { prefix: "/currencies", modules: ["divisas"] },
  { prefix: "/users", modules: ["clientes"] },
  { prefix: "/promotions", modules: ["promociones"] },
  { prefix: "/puntos", modules: ["config-puntos"] },
  { prefix: "/referrals/config", modules: ["config-referidos"] },
  { prefix: "/referrals/tracking", modules: ["referentes"] },
  { prefix: "/point-rules", modules: ["matriz-puntos"] },
  { prefix: "/marketplace", modules: ["recompensas"] },
  { prefix: "/pages/document-requirements", modules: ["documentacion"] },
  { prefix: "/pages/home-banners", modules: ["banners"] },
  { prefix: "/pages/hero-sections", modules: ["hero"] },
  { prefix: "/pages/faq", modules: ["faq"] },
  { prefix: "/roles", modules: ["roles"] },
  { prefix: "/team", modules: ["equipo"] },
  { prefix: "/operating-hours", modules: MENU_MODULES["operating-hours"] },
  { prefix: "/metric-exclusions", modules: ["exclusion-metricas"] },
  { prefix: "/app-tokens", modules: ["tokens"] },
  { prefix: "/core-environments", modules: ["entornos"] },
  { prefix: "/webhook-subscriptions", modules: ["webhooks"] },
  { prefix: "/settings", modules: ["ajustes"] },
  { prefix: "/legal", modules: ["terminos"] },
];

@Injectable({ providedIn: "root" })
export class PermissionService {
  private readonly http = inject(HttpClient);
  readonly permissions = signal<Record<string, string[]>>(this.readCache());

  ensure(): Observable<void> {
    return this.http
      .get<{ data?: Record<string, string[]> }>(`${environment.apiUrl}/auth/permissions`)
      .pipe(
        tap((response) => this.setPermissions(response.data ?? {})),
        map(() => undefined),
        catchError(() => of(undefined)),
      );
  }

  reload(): void {
    this.ensure().subscribe();
  }

  can(moduleId: string, action = "r"): boolean {
    return (this.permissions()[moduleId] ?? []).includes(action);
  }

  canReadMenu(menuId: string): boolean {
    const modules = MENU_MODULES[menuId];
    if (!modules) return true;
    return modules.some((moduleId) => this.can(moduleId, "r"));
  }

  modulesForUrl(url: string): string[] | null {
    const path = url.split("?")[0];
    const match = ROUTE_MODULES.find(
      (route) => path === route.prefix || path.startsWith(`${route.prefix}/`),
    );
    return match?.modules ?? null;
  }

  canReadUrl(url: string): boolean {
    const modules = this.modulesForUrl(url);
    if (!modules) return true;
    return modules.some((moduleId) => this.can(moduleId, "r"));
  }

  firstAllowedPath(): string | null {
    for (const route of ROUTE_MODULES) {
      if (route.modules.some((moduleId) => this.can(moduleId, "r"))) {
        return route.prefix;
      }
    }
    return null;
  }

  private setPermissions(permissions: Record<string, string[]>): void {
    this.permissions.set(permissions);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(permissions));
  }

  private readCache(): Record<string, string[]> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
}
